(() => {
  if (window.FXLMotion) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const disclosures = new WeakMap();
  const popovers = new WeakMap();
  const images = new WeakMap();
  const active = new Set();
  const run = (element, frames, duration, done = () => {}) => {
    if (reduced.matches || !element.animate) { done(); return null; }
    const animation = element.animate(frames, { duration, easing:'cubic-bezier(.22,1,.36,1)', fill:'both' });
    const finish = () => { active.delete(animation); animation.cancel(); done(); };
    animation.onfinish = finish;
    animation.oncancel = () => active.delete(animation);
    active.add(animation);
    return animation;
  };
  reduced.addEventListener('change', () => {
    if (reduced.matches) [...active].forEach(animation => {
      if (animation.playState !== 'idle') animation.finish();
    });
  });

  function disclose(details, open) {
    const state = disclosures.get(details);
    if (!state) { details.open = open; return; }
    if (state.target === open) return;
    const start = details.getBoundingClientRect().height;
    state.animation?.cancel();
    state.target = open;
    state.summary.setAttribute('aria-expanded', String(open));
    details.style.height = '';
    details.style.overflow = 'hidden';
    details.open = true;
    const end = open ? details.getBoundingClientRect().height : state.summary.getBoundingClientRect().height;
    for (const child of details.children) {
      if (child !== state.summary) child.inert = !open;
    }
    if (!open && details.contains(document.activeElement)) state.summary.focus({preventScroll:true});
    const finish = () => {
      details.open = open;
      details.style.height = state.height;
      details.style.overflow = state.overflow;
      state.animation = null;
    };
    state.animation = run(details, [{height:`${start}px`},{height:`${end}px`}], 200, finish);
  }

  function popover(element, open, done = () => {}) {
    let state = popovers.get(element);
    if (!state) { state={target:!element.hidden,animation:null};popovers.set(element,state); }
    if (state.target === open) return;
    const wasHidden = element.hidden;
    const style = getComputedStyle(element);
    const from = { opacity:wasHidden ? 0 : style.opacity, transform:wasHidden ? 'translateY(-5px)' : style.transform };
    state.animation?.cancel();
    state.target = open;
    element.hidden = false;
    element.inert = !open;
    state.animation = run(element, [from,{opacity:open ? 1 : 0,transform:open ? 'translateY(0)' : 'translateY(-5px)'}], open ? 170 : 130, () => {
      element.hidden = !open;
      state.animation = null;
      done();
    });
  }

  function imageView(image, view) {
    let state = images.get(image);
    if (!state) { state={sequence:0,animation:null,ghost:null};images.set(image,state); }
    const sequence = ++state.sequence;
    const src = view.image || '';
    const srcset = view.srcset || '';
    if (image.getAttribute('src') === src && (image.getAttribute('srcset') || '') === srcset) return;
    if (!src) return;
    const next = new Image();
    next.sizes = image.sizes;
    if (srcset) next.srcset = srcset;
    next.src = src;
    next.decode().then(() => {
      if (sequence !== state.sequence || !image.isConnected) return;
      state.animation?.cancel();
      state.ghost?.remove();
      state.ghost = null;
      const parent = image.parentElement;
      if (!reduced.matches && image.complete && image.naturalWidth && parent) {
        const ghost = image.cloneNode();
        ghost.removeAttribute('id');
        for (const attribute of [...ghost.attributes]) {
          if (attribute.name.startsWith('data-')) ghost.removeAttribute(attribute.name);
        }
        ghost.removeAttribute('srcset');
        ghost.src = image.currentSrc || image.src;
        ghost.setAttribute('aria-hidden','true');
        ghost.className = 'fxl-image-previous';
        const style = getComputedStyle(image);
        // Preserve the content box: the old frame must not lose padding or double-apply its transform.
        ghost.style.cssText = `position:absolute;left:${image.offsetLeft}px;top:${image.offsetTop}px;pointer-events:none;z-index:1;margin:0;transition:none;`;
        for (const property of ['width','height','box-sizing','padding','border','border-radius','object-fit','object-position','transform','transform-origin']) {
          ghost.style.setProperty(property, style.getPropertyValue(property));
        }
        parent.append(ghost);
        state.ghost = ghost;
      }
      // The destination link and selected swatch update immediately; only pixels wait for decoding.
      if (srcset) image.setAttribute('srcset',srcset); else image.removeAttribute('srcset');
      image.src = src;
      image.alt = view.alt || '';
      image.removeAttribute('data-preview-src');
      if (state.ghost) {
        const ghost = state.ghost;
        state.animation = run(ghost,[{opacity:1},{opacity:0}],150,()=>{ghost.remove();if(state.ghost===ghost)state.ghost=null;});
      }
    }).catch(() => { /* Keep the last readable image when an asset cannot load. */ });
  }

  function captureCart(cart) {
    if (reduced.matches) return () => {};
    const positions = new Map();
    cart.querySelectorAll('[data-quantity-line-key]').forEach(input => {
      positions.set(input.dataset.quantityLineKey,input.closest('.cart-item')?.getBoundingClientRect().top);
    });
    return () => {
      cart.querySelectorAll('[data-quantity-line-key]').forEach(input => {
        const row=input.closest('.cart-item');
        const previous=positions.get(input.dataset.quantityLineKey);
        if (!row || previous == null) return;
        const shift=previous-row.getBoundingClientRect().top;
        if (Math.abs(shift)>1 && Math.abs(shift)<innerHeight) run(row,[{transform:`translateY(${shift}px)`},{transform:'translateY(0)'}],160);
      });
      const empty=cart.querySelector('.cart__warnings');
      if (empty && cart.classList.contains('is-empty')) run(empty,[{opacity:.7},{opacity:1}],120);
    };
  }

  function initDisclosures(scope = document) {
    scope.querySelectorAll('.mry-header-mobile-nav__shop,.mry-pdp__faq details,.mry-footer__mobile-nav details').forEach(details => {
      if (disclosures.has(details)) return;
      const summary=details.querySelector(':scope > summary');
      if (!summary) return;
      const state={summary,target:details.open,animation:null,group:details.getAttribute('name'),height:details.style.height,overflow:details.style.overflow};
      disclosures.set(details,state);
      details.dataset.fxlDisclosureGroup=state.group || '';
      details.removeAttribute('name');
      summary.setAttribute('aria-expanded',String(details.open));
      summary.addEventListener('click',event=>{
        event.preventDefault();
        const open=!state.target;
        const panel=details.closest('.mry-header-mobile-nav__panel');
        const anchor=summary.getBoundingClientRect().top;
        if (open && state.group) {
          document.querySelectorAll('[data-fxl-disclosure-group]').forEach(other=>{
            if (other!==details && other.dataset.fxlDisclosureGroup===state.group) disclose(other,false);
          });
        }
        disclose(details,open);
        if (panel && !reduced.matches) {
          const start=performance.now();
          const keepAnchor=()=>{
            panel.scrollTop+=summary.getBoundingClientRect().top-anchor;
            if (performance.now()-start<220 && state.target===open) requestAnimationFrame(keepAnchor);
          };
          requestAnimationFrame(keepAnchor);
        }
      });
    });
  }

  function initDesktopNavigation(scope = document) {
    scope.querySelectorAll('.mry-header-desktop-nav__shop').forEach(details=>{
      if (details.dataset.fxlMotionReady) return;
      details.dataset.fxlMotionReady='true';
      const summary=details.querySelector(':scope > summary');
      const menu=details.querySelector('.mry-header-desktop-nav__menu');
      let target=details.open;
      summary.setAttribute('aria-expanded', String(target));
      menu.hidden=!target;
      details.removeAttribute('name');
      const positionMenu = () => {
        const anchor = details.getBoundingClientRect();
        const width = Math.min(640, window.innerWidth - 48);
        const left = Math.max(24, Math.min(anchor.left, window.innerWidth - width - 24));
        menu.style.left = `${left - anchor.left}px`;
        menu.style.maxHeight = `${Math.max(0, (window.visualViewport?.height || innerHeight) - anchor.bottom - 24)}px`;
      };
      const toggle=open=>{
        if (target===open) return;
        target=open;
        summary.setAttribute('aria-expanded',String(open));
        details.open=true;
        if (open) positionMenu();
        popover(menu,open,()=>{if(!target) details.open=false;});
      };
      details.addEventListener('fxl:close',()=>toggle(false));
      summary.addEventListener('click',event=>{
        event.preventDefault();
        const open=!target;
        if(open) document.querySelectorAll('.mry-header-desktop-nav__shop').forEach(other=>{if(other!==details)other.dispatchEvent(new Event('fxl:close'));});
        toggle(open);
      });
      details.addEventListener('keydown',event=>{
        if(event.key==='Escape' && target){event.preventDefault();event.stopPropagation();toggle(false);summary.focus({preventScroll:true});}
      },true);
      details.addEventListener('focusout',event=>{if(!details.contains(event.relatedTarget))toggle(false);});
      window.matchMedia('(min-width: 990px)').addEventListener('change', event => {
        if (!event.matches) toggle(false);
      });
      window.addEventListener('resize', () => { if (target) positionMenu(); });
    });
  }
  document.addEventListener('click',event=>{
    if(!event.target.closest('.mry-header-desktop-nav__shop')) document.querySelectorAll('.mry-header-desktop-nav__shop').forEach(details=>details.dispatchEvent(new Event('fxl:close')));
  });

  function initMarquee(scope = document) {
    scope.querySelectorAll('.mryp-promises').forEach(section=>{
      if (section.dataset.fxlMotionReady) return;
      const track=section.querySelector('.mryp-promises__track');
      const line=section.querySelector('.mryp-promises__line');
      const button=section.querySelector('[data-fxl-marquee-toggle]');
      if (!track || !line || !button) return;
      section.dataset.fxlMotionReady='true';
      button.hidden=false;
      let paused=false, visible=false;
      const update=()=>{
        track.style.animationPlayState=paused || !visible || document.hidden ? 'paused' : 'running';
        button.setAttribute('aria-pressed',String(paused));
        button.setAttribute('aria-label',paused ? 'Resume scrolling benefits' : 'Pause scrolling benefits');
        button.title=paused ? 'Resume scrolling benefits' : 'Pause scrolling benefits';
        button.hidden=false;
        section.classList.toggle('is-paused',paused);
      };
      const measure=()=>track.style.setProperty('--fxl-marquee-duration',`${Math.max(1,line.getBoundingClientRect().width/42)}s`);
      const resize=new ResizeObserver(measure);resize.observe(line);
      const observer=new IntersectionObserver(entries=>{visible=entries[0].isIntersecting;update();});observer.observe(section);
      button.addEventListener('click',()=>{paused=!paused;update();});
      document.addEventListener('visibilitychange',update);
      section.addEventListener('fxl:dispose',()=>{resize.disconnect();observer.disconnect();document.removeEventListener('visibilitychange',update);},{once:true});
      measure();update();
    });
  }
  window.FXLMotion={disclose,popover,imageView,captureCart,run};
  function initPolicyTables(scope) {
    scope.querySelectorAll('.shopify-policy__body table').forEach(table => {
      if (table.parentElement.classList.contains('fxl-policy-table-scroll')) return;
      const wrap = document.createElement('div');
      wrap.className = 'fxl-policy-table-scroll';
      wrap.setAttribute('role', 'region');
      wrap.setAttribute('aria-label', 'Scrollable policy table');
      table.before(wrap);
      wrap.append(table);
      const sync = () => { wrap.tabIndex = wrap.scrollWidth > wrap.clientWidth ? 0 : -1; };
      new ResizeObserver(sync).observe(wrap);
      sync();
      table.closest('.shopify-policy__body')?.classList.add('fxl-policy-tables-ready');
    });
  }
  // Keep the existing teaser enabled, but reserve the purchase bar's space.
  function initTeaserClearance() {
    const bar = document.querySelector('[data-sticky-bar]');
    if (!bar) return;
    const bound = new WeakSet();
    const widgets = new Set();
    const update = () => {
      const bottom = Number.parseFloat(getComputedStyle(bar).bottom) || 0;
      const clearance = bar.hidden ? 0 : Math.max(0, bar.offsetHeight + bottom + 8);
      widgets.forEach(host => host.style.setProperty('--fxl-teaser-safe-bottom', `${clearance}px`));
    };
    const bind = host => {
      if (bound.has(host) || !host.shadowRoot) return;
      bound.add(host); widgets.add(host);
      const ensure = () => {
        if (!host.shadowRoot.querySelector('[data-fxl-teaser-clearance]')) {
          const style = document.createElement('style');
          style.dataset.fxlTeaserClearance = '';
          style.textContent = '[data-form-root][data-current-step="tease"]{bottom:var(--fxl-teaser-safe-bottom,0px)!important;}';
          host.shadowRoot.append(style);
        }
        update();
      };
      new MutationObserver(ensure).observe(host.shadowRoot, { childList: true, subtree: true });
      ensure();
    };
    const scan = () => document.querySelectorAll('shopify-forms-embed').forEach(bind);
    new MutationObserver(records => {
      if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches('shopify-forms-embed') || node.querySelector('shopify-forms-embed'))))) scan();
    }).observe(document.body, { childList: true, subtree: true });
    new MutationObserver(update).observe(bar, { attributes: true, attributeFilter: ['hidden', 'class'] });
    new ResizeObserver(update).observe(bar);
    window.addEventListener('resize', update);
    customElements.whenDefined('shopify-forms-embed').then(scan);
    scan();
  }
  const init=scope=>{initDisclosures(scope);initDesktopNavigation(scope);initMarquee(scope);initPolicyTables(scope);};
  init(document);
  initTeaserClearance();
  document.addEventListener('shopify:section:load',event=>init(event.target));
  document.addEventListener('shopify:section:unload',event=>{
    event.target.querySelectorAll('.mryp-promises').forEach(section=>section.dispatchEvent(new Event('fxl:dispose')));
  });
})();
